/*
  Plyr on demand.

  The Plyr library (JS + CSS) and the Vimeo/YouTube iframe are no longer loaded with the page:
  - <plyr-player data-video-autoplay="false"> shows a facade (poster + play button) and only
    loads Plyr when the facade is clicked, then starts the video.
  - <plyr-player data-video-autoplay="true"> waits until the page has finished loading (+ idle)
    AND the player is within ~300px of the viewport, then loads Plyr and autoplays as before.

  Shared helpers exposed on window (used by global.js DeferredMedia and docuseries.js):
  - window.loadPlyr()            -> Promise resolving with window.Plyr (loaded once, shared)
  - window.whenPageIdle()        -> Promise resolving after window load + idle time
  - window.getVideoThumbnail()   -> Promise resolving with { url, width, height } for a Vimeo/YouTube id
  - window.onNearViewport(el, cb)-> calls cb once when el gets within ~300px of the viewport
*/
(function () {
  if (!window.loadPlyr) {
    let plyrPromise = null;

    const loadScript = (src) =>
      new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
          script.remove();
          reject(new Error(`Failed to load ${src}`));
        };
        document.head.appendChild(script);
      });

    const loadStyle = (href) =>
      new Promise((resolve) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.onload = () => resolve();
        // A missing stylesheet should never block the video from playing
        link.onerror = () => resolve();
        document.head.appendChild(link);
      });

    window.loadPlyr = function loadPlyr() {
      if (plyrPromise) return plyrPromise;

      const assets = window.plyrAssets || {};
      const hasPlyrCss = !!document.querySelector('link[href*="plyr-v3"], link[href*="plyr.css"]');
      const cssPromise = assets.css && !hasPlyrCss ? loadStyle(assets.css) : Promise.resolve();

      let jsPromise;
      if (typeof window.Plyr !== 'undefined') {
        jsPromise = Promise.resolve();
      } else if (assets.js) {
        jsPromise = loadScript(assets.js);
      } else {
        jsPromise = Promise.reject(new Error('Plyr asset URL is missing (window.plyrAssets)'));
      }

      plyrPromise = Promise.all([jsPromise, cssPromise]).then(() => {
        if (typeof window.Plyr === 'undefined') throw new Error('Plyr is not available');
        return window.Plyr;
      });

      // Allow a retry (e.g. a flaky connection) on the next call
      plyrPromise.catch(() => {
        plyrPromise = null;
      });

      return plyrPromise;
    };
  }

  if (!window.whenPageIdle) {
    let idlePromise = null;

    window.whenPageIdle = function whenPageIdle() {
      if (idlePromise) return idlePromise;

      idlePromise = new Promise((resolve) => {
        const afterLoad = () => {
          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => resolve(), { timeout: 3000 });
          } else {
            // Safari: no requestIdleCallback
            setTimeout(resolve, 1500);
          }
        };

        if (document.readyState === 'complete') {
          afterLoad();
        } else {
          window.addEventListener('load', afterLoad, { once: true });
        }
      });

      return idlePromise;
    };
  }

  if (!window.getVideoThumbnail) {
    const thumbnailCache = {};

    window.getVideoThumbnail = function getVideoThumbnail(provider, id, url) {
      if (!id) return Promise.reject(new Error('Missing video id'));

      const key = `${provider}:${id}`;
      if (thumbnailCache[key]) return thumbnailCache[key];

      if (provider === 'youtube') {
        thumbnailCache[key] = Promise.resolve({ url: `https://i.ytimg.com/vi/${id}/hqdefault.jpg` });
      } else {
        // Use the full URL when we have it: unlisted Vimeo videos need their private hash
        const videoUrl = url && /^https?:\/\/(www\.)?vimeo\.com\//.test(url) ? url : `https://vimeo.com/${id}`;
        // No ?width= here: with it, oEmbed reports the wrong shape (4:3 for 16:9 videos). Without it,
        // width/height have the video's real proportions and the thumbnail is a small 16:9 crop
        // (..._295x166), which is swapped for the full-size version (..._1280, the video's own shape).
        thumbnailCache[key] = fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(videoUrl)}`)
          .then((response) => {
            if (!response.ok) throw new Error(`oEmbed ${response.status}`);
            return response.json();
          })
          .then((data) => {
            if (!data || !data.thumbnail_url) throw new Error('No thumbnail');
            return {
              url: data.thumbnail_url.replace(/_\d+x\d+(?=\?|$)/, '_1280'),
              width: data.width,
              height: data.height
            };
          });
      }

      return thumbnailCache[key];
    };
  }

  if (!window.onNearViewport) {
    window.onNearViewport = function onNearViewport(element, callback, rootMargin = '300px 0px') {
      if (!('IntersectionObserver' in window)) {
        callback();
        return () => {};
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          callback();
        },
        { rootMargin }
      );
      observer.observe(element);

      return () => observer.disconnect();
    };
  }

  if (customElements.get('plyr-player')) return;

  const VIMEO_ORIGIN = 'https://player.vimeo.com';

  /*
    <plyr-player> modes (markup from snippets/video-block.liquid):
    - data-video-background="true": autoplay, no controls. Starts once the page has loaded and the
      player is near the screen (or straight away with data-eager). A poster shows until it plays.
        data-provider="html5": plain <video muted loop> (no Plyr), paused while off screen.
        data-provider="vimeo": Vimeo "background" iframe (no Plyr).
    - otherwise Plyr: a facade (poster + play button) loads Plyr on click; with autoplay it loads
      after page load when near the screen. data-provider="html5" wraps a <video>, "vimeo" an embed.
  */
  class PlyrPlayer extends HTMLElement {
    constructor() {
      super();

      this.autoplay = this.dataset.videoAutoplay === 'true';
      this.controls = this.dataset.videoControls === 'true';
      this.background = this.dataset.videoBackground === 'true';
      this.provider = this.dataset.provider || 'vimeo';
      this.mqlSmall = window.matchMedia('screen and (max-width: 749px)');
    }

    connectedCallback() {
      if (this.isSetUp) return;
      this.isSetUp = true;

      this.plyrPlayerElement = this.querySelector('[data-plyr-player]');
      this.facade = this.querySelector('[data-video-facade]');
      this.poster = this.querySelector('[data-video-facade-poster], .video-facade__poster');

      // Pick the mobile video once, before anything is requested
      const isSmall = this.mqlSmall.matches;
      if (this.provider === 'html5') {
        const useMobile = isSmall && !!this.dataset.videoSrcMobile;
        this.videoSrc = useMobile ? this.dataset.videoSrcMobile : this.dataset.videoSrc;
        this.videoRatio = parseFloat(useMobile ? this.dataset.videoRatioMobile : this.dataset.videoRatio) || 0;
        this.posterUrl = (isSmall && this.dataset.posterMobile) || this.dataset.poster || '';
        this.videoUrl = this.videoSrc;
      } else {
        this.embedId = (this.plyrPlayerElement && this.plyrPlayerElement.dataset.plyrEmbedId) || this.dataset.embedId;
        this.videoUrl = this.dataset.videoUrl;
        if (isSmall && this.dataset.videoMobile) {
          this.embedId = this.dataset.videoMobile;
          this.videoUrl = this.dataset.videoMobileUrl;
          if (this.plyrPlayerElement) this.plyrPlayerElement.setAttribute('data-plyr-embed-id', this.embedId);
        }
      }

      if (this.background) {
        this.setUpBackground();
        return;
      }

      if (!this.plyrPlayerElement) return;

      // Already built (e.g. markup cloned from an initialised player)
      if (this.querySelector('.plyr')) {
        this.removeFacade();
        return;
      }

      window.onNearViewport(this, () => this.onNearViewport());

      if (this.autoplay || !this.facade) {
        // Autoplay (or legacy markup without a facade): load after page load + idle, when close to the viewport
        Promise.all([this.nearViewport(), window.whenPageIdle()]).then(() => this.load(false));
      } else {
        this.onFacadeClick = this.onFacadeClick.bind(this);
        this.facade.addEventListener('click', this.onFacadeClick);
      }
    }

    disconnectedCallback() {
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (this.onVimeoMessage) window.removeEventListener('message', this.onVimeoMessage);
      clearTimeout(this.revealTimer);
    }

    nearViewport() {
      if (!this.nearViewportPromise) {
        this.nearViewportPromise = new Promise((resolve) => {
          this.resolveNearViewport = resolve;
        });
      }
      return this.nearViewportPromise;
    }

    onNearViewport() {
      this.nearViewport();
      this.resolveNearViewport();
      this.loadPoster();
    }

    // Vimeo/YouTube thumbnail as the poster, unless one was rendered by Liquid (Shopify videos always
    // have one: their preview image or the custom poster). For Vimeo this also gives the video's real
    // shape, which sizes the box now and is handed to Plyr later (see initializePlayer), even when a
    // custom poster is set, so nothing jumps when the video starts.
    loadPoster() {
      if (this.posterRequested) return;
      this.posterRequested = true;

      if (this.provider === 'html5' || !this.embedId) return;

      const poster = this.poster;
      const posterSet = this.dataset.posterSet === 'true';
      const provider = this.plyrPlayerElement && this.plyrPlayerElement.dataset.plyrProvider === 'youtube' ? 'youtube' : 'vimeo';
      // YouTube's thumbnail is always 4:3 letterboxed, so it has nothing to add to a custom poster
      if (provider === 'youtube' && posterSet) return;

      window
        .getVideoThumbnail(provider, this.embedId, this.videoUrl)
        .then((thumbnail) => {
          if (!this.isConnected) return;

          // Background videos keep their container's ratio, as before. YouTube keeps the 16:9 default.
          if (!this.background && !this.dataset.videoRatio && provider !== 'youtube' && thumbnail.width && thumbnail.height) {
            this.detectedRatio = thumbnail.width / thumbnail.height;
            this.style.setProperty('--video-facade-ratio', String(this.detectedRatio));
            // The shape is for the video picked for this screen size, so it replaces a mobile poster's shape too
            this.style.removeProperty('--video-facade-ratio-mobile');
          }

          if (posterSet || !poster || poster.getAttribute('src')) return;
          poster.addEventListener('load', () => poster.classList.add('is-loaded'), { once: true });
          poster.src = thumbnail.url;
          poster.hidden = false;
        })
        .catch(() => {
          // Keep the neutral background
        });
    }

    /* Background videos (autoplay, no controls) */

    setUpBackground() {
      if (this.dataset.eager === 'true' || (window.Shopify && window.Shopify.designMode)) {
        this.loadPoster();
        this.startBackground();
        return;
      }

      window.onNearViewport(this, () => this.onNearViewport());
      Promise.all([this.nearViewport(), window.whenPageIdle()]).then(() => this.startBackground());
    }

    startBackground() {
      if (this.backgroundStarted) return;
      this.backgroundStarted = true;

      if (this.provider === 'html5') {
        this.startNativeBackground();
      } else {
        this.startVimeoBackground();
      }
    }

    startNativeBackground() {
      const video = this.querySelector('[data-native-video]');
      if (!video || !this.videoSrc) return;

      this.video = video;
      video.muted = true;
      video.addEventListener('playing', () => this.classList.add('is-playing'), { once: true });
      video.src = this.videoSrc;

      // Play while on screen, pause while scrolled away (saves battery and CPU)
      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.playNativeBackground();
          } else {
            video.pause();
          }
        });
      });
      this.visibilityObserver.observe(this);
    }

    playNativeBackground() {
      const attempt = this.video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    }

    startVimeoBackground() {
      if (!this.embedId) return;

      const params = new URLSearchParams({ background: '1', muted: '1', autopause: '0', controls: '0' });
      // Phones get Vimeo's automatic quality instead of a forced 1080p stream
      if (!this.mqlSmall.matches) params.set('quality', '1080p');

      const iframe = document.createElement('iframe');
      iframe.className = 'video-background__iframe';
      iframe.src = `${VIMEO_ORIGIN}/video/${this.embedId}?${params.toString()}`;
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
      iframe.setAttribute('tabindex', '-1');
      iframe.setAttribute('aria-hidden', 'true');

      // Show the iframe (over the poster) once the video is actually playing
      const reveal = () => {
        clearTimeout(this.revealTimer);
        this.classList.add('is-playing');
        window.removeEventListener('message', this.onVimeoMessage);
      };

      this.onVimeoMessage = (event) => {
        if (event.origin !== VIMEO_ORIGIN || event.source !== iframe.contentWindow) return;

        let data = event.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            return;
          }
        }
        if (!data) return;

        if (data.event === 'ready') {
          ['playing', 'timeupdate'].forEach((value) => {
            iframe.contentWindow.postMessage(JSON.stringify({ method: 'addEventListener', value }), VIMEO_ORIGIN);
          });
        } else if (data.event === 'playing' || data.event === 'timeupdate') {
          reveal();
        }
      };
      window.addEventListener('message', this.onVimeoMessage);

      // Fallback if Vimeo never reports playback
      iframe.addEventListener('load', () => {
        this.revealTimer = setTimeout(reveal, 4000);
      }, { once: true });

      this.appendChild(iframe);
    }

    /* Plyr players */

    onFacadeClick(event) {
      event.preventDefault();
      this.load(true);
    }

    load(fromClick) {
      if (this.loadPromise) return this.loadPromise;

      if (this.facade) {
        this.facade.classList.add('is-loading');
        this.facade.setAttribute('aria-busy', 'true');
      }

      this.loadPromise = window
        .loadPlyr()
        .then(() => this.initializePlayer(fromClick))
        .catch((error) => {
          console.error(error);
          this.loadPromise = null;
          if (this.facade) {
            this.facade.classList.remove('is-loading');
            this.facade.removeAttribute('aria-busy');
          }
        });

      return this.loadPromise;
    }

    removeFacade() {
      if (!this.facade) return;
      this.facade.remove();
      this.facade = null;
      this.classList.remove('is-player-built');
    }

    // Shopify videos: replace the placeholder with a <video> for Plyr to wrap
    createNativeVideo() {
      const placeholder = this.plyrPlayerElement;
      const video = document.createElement('video');
      video.id = placeholder.id;
      video.setAttribute('playsinline', '');
      video.preload = 'metadata';
      if (this.posterUrl) video.poster = this.posterUrl;
      if (this.autoplay) {
        video.muted = true;
        video.loop = true;
      }
      video.src = this.videoSrc;
      placeholder.replaceWith(video);
      this.plyrPlayerElement = video;
      return video;
    }

    initializePlayer(fromClick) {
      const isNative = this.provider === 'html5';
      const plyrPlayerElement = isNative ? this.createNativeVideo() : this.plyrPlayerElement;
      const plyrPlayerId = plyrPlayerElement.id;

      let cfg = {
        fullscreen: {
          enabled: true,
          fallback: true,
          iosNative: true,
          container: null
        }
      };

      if (this.autoplay) {
        Object.assign(cfg, {
          autoplay: true,
          fullscreen: {
            enabled: false,
            fallback: false,
            iosNative: false,
            container: null
          },
          loop: {
            active: true
          },
          muted: true
        });
      }

      if (!this.controls) {
        Object.assign(cfg, {
          controls: []
        });
      }

      // Give Plyr the video's shape up front. Otherwise it only sizes the player once Vimeo reports
      // the video's size (or the <video> loads its metadata), and the box jumps in between because
      // the facade has stopped holding the height. Known shapes: the Shopify video's own ratio, the
      // "Vimeo video ratio" setting, or the Vimeo thumbnail (same proportions as the video).
      const knownRatio = isNative ? this.videoRatio : parseFloat(this.dataset.videoRatio) || this.detectedRatio || 0;
      if (knownRatio) {
        cfg.ratio = `${Math.round(knownRatio * 1000)}:1000`;
      } else if (this.facade) {
        // Shape still unknown (Vimeo didn't answer in time): hold the current height until the video
        // plays, so the box changes at most once, from the poster's shape to the video's.
        this.style.height = `${this.offsetHeight}px`;
        this.style.overflow = 'hidden';
      }

      let plyrPlayerVideoUrl = this.videoSrc;
      if (!isNative) {
        const plyrPlayerEmbedId = plyrPlayerElement.dataset.plyrEmbedId;
        plyrPlayerVideoUrl = `https://vimeo.com/${plyrPlayerEmbedId}`;
        if (plyrPlayerElement.dataset.plyrProvider === 'youtube')
          plyrPlayerVideoUrl = `https://www.youtube.com/watch?v=${plyrPlayerEmbedId}`;
      }

      const plyrPlayer = new Plyr(plyrPlayerElement, cfg);
      plyrCustomEvents.addEvents(plyrPlayer);

      // The real player now reserves the space: lay the facade over it until the video shows
      if (this.facade) this.classList.add('is-player-built');

      let facadeTimer = null;
      const hideFacade = () => {
        clearTimeout(facadeTimer);
        this.removeFacade();
        this.style.height = '';
        this.style.overflow = '';
      };
      plyrPlayer.once('playing', hideFacade);
      plyrPlayer.once('ready', () => {
        if (fromClick) this.playFromClick(plyrPlayer);
        // Never leave the facade over the player (e.g. playback blocked): Plyr's own controls take over
        facadeTimer = setTimeout(hideFacade, fromClick ? 2500 : 4000);
      });

      const plyrInstance = {
        id: plyrPlayerId,
        url: plyrPlayerVideoUrl,
        player: plyrPlayer
      };

      window.plyrInstances = window.plyrInstances || [];
      window.plyrInstances.push(plyrInstance);

      // analytics-events.js attaches to window.plyrInstances on DOMContentLoaded only:
      // players created after that need to be attached here
      if (document.readyState !== 'loading' && window.analyticsEvents && window.analyticsEvents.attachPlyrVideoEvents) {
        window.analyticsEvents.attachPlyrVideoEvents(plyrInstance);
      }

      this.player = plyrPlayer;
      return plyrPlayer;
    }

    // Start the video the visitor just asked for. Sound is attempted first; if the browser
    // refuses (e.g. iOS when the click "gesture" has expired while Plyr was loading), retry muted.
    playFromClick(player) {
      const playMuted = () => {
        player.muted = true;
        const retry = player.play();
        if (retry && typeof retry.catch === 'function') retry.catch(() => {});
      };

      let attempt;
      try {
        attempt = player.play();
      } catch (e) {
        playMuted();
        return;
      }

      const checkPlaying = () => {
        setTimeout(() => {
          if (!player.playing) playMuted();
        }, 800);
      };

      if (attempt && typeof attempt.then === 'function') {
        attempt.then(checkPlaying).catch(playMuted);
      } else {
        checkPlaying();
      }
    }
  }

  customElements.define('plyr-player', PlyrPlayer);
})();
